/**
 * Shared utilities for Crr Time of Day chart components
 */

export const CRR_CHART_COLORS = [
  '#2563eb', '#16a34a', '#f97316', '#dc2626', '#7c3aed',
  '#0891b2', '#f59e0b', '#8b5cf6', '#10b981', '#6366f1',
];

export const MARKER_SYMBOLS = ['circle', 'triangle-up', 'square', 'diamond', 'x'];

export const SYMBOL_MAP: { [key: string]: string } = {
  circle: '●',
  'triangle-up': '▲',
  square: '■',
  diamond: '◆',
  x: '✕',
};

/**
 * Get tyre name from equipment catalog
 */
export const getTyreName = (tyreId: string, equipmentCatalog: any): string => {
  if (equipmentCatalog && equipmentCatalog.tyres && Array.isArray(equipmentCatalog.tyres)) {
    const tyre = equipmentCatalog.tyres.find((t: any) => t.id === tyreId);
    if (tyre) {
      return tyre.name || tyre.designation || `Tyre ${tyreId.slice(0, 8)}`;
    }
  }
  return `Tyre ${tyreId.slice(0, 8)}`;
};

/**
 * Get combined tyre name (front / rear) or just rear if same
 */
export const getCombinedTyreName = (
  frontId: string | null | undefined,
  rearId: string,
  equipmentCatalog: any
): string => {
  const rearName = getTyreName(rearId, equipmentCatalog);
  if (!frontId || frontId === rearId) {
    return rearName;
  }
  const frontName = getTyreName(frontId, equipmentCatalog);
  return `${frontName} / ${rearName}`;
};

export interface TyreDataMap {
  [tyreName: string]: {
    pressures: number[];
    configs: any[];
  };
}

export interface TyrePressureMarkers {
  [tyreName: string]: {
    [pressure: number]: string;
  };
}

export interface TyreColorMap {
  [tyreName: string]: string;
}

export interface CustomLegendData {
  [tyreName: string]: {
    pressures: number[];
    colors: string[];
    symbols: string[];
  };
}

/**
 * Build tyre data map from session configs
 */
export const buildTyreDataMap = (
  configs: any[],
  equipmentCatalog: any
): TyreDataMap => {
  const tyreData: TyreDataMap = {};

  configs.forEach((config: any) => {
    if (!config.runs || config.runs.length === 0) return;
    if (config.settings_collection?.exclude_tyre_report) return;

    const tyreId = config.tyre_rear_id;
    const tyrePressure = config.tyre_rear_pressure;
    if (!tyreId || tyrePressure === null || tyrePressure === undefined) return;

    const tyreName = getCombinedTyreName(config.tyre_front_id, tyreId, equipmentCatalog);

    if (!tyreData[tyreName]) {
      tyreData[tyreName] = { pressures: [], configs: [] };
    }

    if (!tyreData[tyreName].pressures.includes(tyrePressure)) {
      tyreData[tyreName].pressures.push(tyrePressure);
    }
    tyreData[tyreName].configs.push({ ...config, tyrePressure, tyreName });
  });

  return tyreData;
};

/**
 * Assign marker symbols to pressure levels for each tyre type
 */
export const assignMarkerSymbols = (tyreData: TyreDataMap): TyrePressureMarkers => {
  const tyrePressureMarkers: TyrePressureMarkers = {};

  Object.entries(tyreData).forEach(([tyreName, data]) => {
    const sortedPressures = [...data.pressures].sort((a, b) => a - b);
    tyrePressureMarkers[tyreName] = {};
    sortedPressures.forEach((pressure, index) => {
      const markerIndex = Math.min(index, MARKER_SYMBOLS.length - 1);
      tyrePressureMarkers[tyreName][pressure] = MARKER_SYMBOLS[markerIndex];
    });
  });

  return tyrePressureMarkers;
};

/**
 * Assign colors to tyre types
 */
export const buildColorMap = (tyreData: TyreDataMap): TyreColorMap => {
  const tyreColorMap: TyreColorMap = {};
  let colorIndex = 0;

  Object.keys(tyreData).forEach((tyreName) => {
    tyreColorMap[tyreName] = CRR_CHART_COLORS[colorIndex % CRR_CHART_COLORS.length];
    colorIndex++;
  });

  return tyreColorMap;
};

/**
 * Build custom legend data structure
 */
export const buildCustomLegendData = (
  configs: any[],
  equipmentCatalog: any,
  tyreColorMap: TyreColorMap,
  tyrePressureMarkers: TyrePressureMarkers
): CustomLegendData => {
  const customLegendData: CustomLegendData = {};

  configs?.forEach((config: any) => {
    if (config.settings_collection?.exclude_tyre_report) return;
    const tyrePressure = config.tyre_rear_pressure;
    const tyreId = config.tyre_rear_id;
    if (!tyrePressure || !tyreId) return;

    const tyreName = getCombinedTyreName(config.tyre_front_id, tyreId, equipmentCatalog);
    const color = tyreColorMap[tyreName];
    const symbol = tyrePressureMarkers[tyreName]?.[tyrePressure] || 'circle';

    if (!customLegendData[tyreName]) {
      customLegendData[tyreName] = { pressures: [], colors: [], symbols: [] };
    }

    if (!customLegendData[tyreName].pressures.includes(tyrePressure)) {
      customLegendData[tyreName].pressures.push(tyrePressure);
      customLegendData[tyreName].colors.push(color);
      customLegendData[tyreName].symbols.push(symbol);
    }
  });

  return customLegendData;
};

/**
 * Generate custom legend annotations for Plotly
 */
export const generateLegendAnnotations = (
  customLegendData: CustomLegendData,
  pressureUnit: 'Bar' | 'PSI'
): any[] => {
  const annotations: any[] = [];
  const pressureLabel = pressureUnit === 'PSI' ? 'PSI' : 'Bar';

  // Flatten all entries into a single list for 3-column layout
  const entries: {
    text: string; color: string; symbol: string;
  }[] = [];

  Object.entries(customLegendData).forEach(([tyreName, data]) => {
    if (
      !data || typeof data !== 'object'
      || !('pressures' in data)
      || !Array.isArray(data.pressures)
    ) return;

    data.pressures.forEach(
      (pressure: number, idx: number) => {
        const color = data.colors?.[idx] || '#333';
        const symbol = data.symbols?.[idx] || 'circle';
        const letter = (data as any).letters?.[idx] || '';
        const dp = pressureUnit === 'PSI'
          ? Math.round(pressure / 0.06895) : pressure;
        const letterLabel = letter ? ` ${letter}` : '';
        entries.push({
          text: `${tyreName} ${dp}${pressureLabel}`
            + `${letterLabel}`,
          color,
          symbol,
        });
      },
    );
  });

  // 3-column layout
  const cols = 3;
  const colWidth = 1.0 / cols;
  const legendY = -0.30;
  const rowHeight = 0.08;

  entries.forEach((entry, idx) => {
    const col = idx % cols;
    const row = Math.floor(idx / cols);
    const xPos = col * colWidth + 0.02;
    const yPos = legendY - row * rowHeight;

    const sym = SYMBOL_MAP[entry.symbol] || '●';
    annotations.push({
      text: `<span style="color: ${entry.color};`
        + ` font-size: 16px;">${sym}</span>`
        + `&nbsp;${entry.text}`,
      x: xPos,
      y: yPos,
      xref: 'paper',
      yref: 'paper',
      xanchor: 'left',
      yanchor: 'middle',
      font: { family: 'Poppins', size: 12, color: '#333' },
      showarrow: false,
    });
  });

  return annotations;
};

export interface CrrChartLayoutOptions {
  title: string;
  yAxisTitle: string;
  yAxisRange?: [number, number];
  height: number;
  annotations: any[];
}

/**
 * Create standard Plotly layout for Crr charts
 */
export const createCrrChartLayout = (options: CrrChartLayoutOptions): any => {
  const layout: any = {
    title: { text: options.title, font: { family: 'Poppins', size: 16 } },
    xaxis: {
      title: { text: 'Time', font: { family: 'Poppins' } },
      type: 'date',
      tickformat: '%H:%M',
    },
    yaxis: {
      title: { text: options.yAxisTitle, font: { family: 'Poppins' } },
    },
    showlegend: false,
    annotations: options.annotations,
    margin: { l: 120, r: 40, t: 80, b: 160 },
    hovermode: 'closest',
    plot_bgcolor: '#f8f9fa',
    paper_bgcolor: 'white',
    font: { family: 'Poppins' },
    height: options.height,
  };

  if (options.yAxisRange) {
    layout.yaxis.range = options.yAxisRange;
    layout.yaxis.autorange = false;
  }

  return layout;
};

/**
 * Standard Plotly config for Crr charts
 */
export const CRR_CHART_PLOTLY_CONFIG = {
  displayModeBar: true,
  modeBarButtonsToRemove: ['lasso2d', 'select2d'],
  displaylogo: false,
  responsive: true,
};

/**
 * Calculate Y-axis range with fixed span centered on data
 */
export const calculateFixedSpanYAxisRange = (
  traces: any[],
  fixedSpan: number
): [number, number] => {
  const allYValues = traces
    .flatMap((trace: any) => trace.y || [])
    .filter((v: any) => v != null && isFinite(v));
  if (allYValues.length === 0) {
    return [0, fixedSpan];
  }
  const yMin = Math.min(...allYValues);
  const yMax = Math.max(...allYValues);
  const dataSpan = yMax - yMin;
  // Use the larger of fixedSpan or actual data range (with padding)
  const effectiveSpan = Math.max(fixedSpan, dataSpan * 1.1);
  const yCenter = (yMin + yMax) / 2;
  return [yCenter - effectiveSpan / 2, yCenter + effectiveSpan / 2];
};

/**
 * Create a trace for average points with markers and lines
 */
export const createAveragePointsTrace = (
  averagePoints: { x: string; y: number; run: number }[],
  options: {
    tyreName: string;
    tyrePressure: number;
    color: string;
    markerSymbol: string;
    legendName: string;
    hoverLabel: string;
    traceSuffix?: string;
  }
): any | null => {
  if (averagePoints.length === 0) {
    return null;
  }

  // Sort by time
  const sortedPoints = [...averagePoints].sort(
    (a, b) => new Date(a.x).getTime() - new Date(b.x).getTime()
  );

  return {
    x: sortedPoints.map((p) => p.x),
    y: sortedPoints.map((p) => p.y),
    mode: 'markers+lines',
    type: 'scatter',
    name: `${options.tyreName}_${options.tyrePressure}_${options.traceSuffix || 'avg'}`,
    legendgroup: options.tyreName,
    marker: {
      color: options.color,
      size: 14,
      opacity: 1.0,
      symbol: options.markerSymbol,
      line: { width: 2, color: 'white' },
    },
    line: { color: options.color, width: 3, dash: 'solid' },
    hovertemplate:
      `<b>${options.legendName}</b><br>` +
      'Time: %{x}<br>' +
      `${options.hoverLabel}: %{y:.5f}<br>` +
      'Run: %{customdata}<br>' +
      '<extra></extra>',
    customdata: sortedPoints.map((p) => p.run),
    showlegend: false,
  };
};
